import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Box,
	Container,
	Flex,
	SimpleGrid,
	Text,
	Link,
	Icon,
	Divider,
	Button,
	HStack,
	VStack,
	Img,
	Badge,
} from '@chakra-ui/react';
import { FiArrowRight, FiTwitter, FiLinkedin, FiInstagram, FiMail } from 'react-icons/fi';
import OpPilotCRMLogo from '../../../assets/logoonlyonwhitebg.png';
import FilmGrain from '../../../pages/OfflinePages/HomePage/FilmGrain';
import {
	NAV_LINKS,
	MONEY_BACK_GUARANTEE,
} from '../../../pages/OfflinePages/HomePage/homePageContent';

const footerLinkSx = {
	color: 'whiteAlpha.800',
	fontSize: 'sm',
	fontWeight: '500',
	transition: 'color 0.2s ease',
	_hover: { color: 'cyan.300', textDecoration: 'none' },
};

const SocialButton = ({ href, label, icon }) => (
	<Link
		href={href}
		isExternal
		aria-label={label}
		display='flex'
		alignItems='center'
		justifyContent='center'
		w={10}
		h={10}
		borderRadius='full'
		bg='whiteAlpha.100'
		border='1px solid'
		borderColor='whiteAlpha.200'
		color='whiteAlpha.900'
		transition='all 0.2s ease'
		_hover={{
			bg: 'cyan.400',
			color: 'gray.900',
			borderColor: 'cyan.400',
			transform: 'translateY(-2px)',
		}}>
		<Icon as={icon} boxSize={4} />
	</Link>
);

const Footer = () => {
	const navigate = useNavigate();
	const year = new Date().getFullYear();

	const scrollTo = (href) => {
		if (href.startsWith('#')) {
			document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
			return;
		}
		navigate(href);
	};

	return (
		<Box
			as='footer'
			position='relative'
			overflow='hidden'
			bg='#0b1220'
			color='white'
			pt={{ base: 14, md: 18 }}
			pb={8}>
			<Box
				position='absolute'
				inset={0}
				bgGradient='linear(180deg, #0f172a 0%, #0a1628 45%, #060d18 100%)'
				pointerEvents='none'
			/>
			<Box
				position='absolute'
				top='-120px'
				right='-80px'
				w='320px'
				h='320px'
				borderRadius='full'
				bg='cyan.500'
				opacity={0.08}
				filter='blur(80px)'
				pointerEvents='none'
			/>
			<Box
				position='absolute'
				bottom='-80px'
				left='-60px'
				w='260px'
				h='260px'
				borderRadius='full'
				bg='blue.600'
				opacity={0.06}
				filter='blur(70px)'
				pointerEvents='none'
			/>
			<FilmGrain opacity={0.035} />

			<Container maxW='container.xl' position='relative' zIndex={1} py={{ base: 10, md: 16 }}>
				<SimpleGrid
					columns={{ base: 1, md: 2, lg: 12 }}
					spacing={{ base: 10, lg: 8 }}
					mb={12}>
					<Box gridColumn={{ lg: 'span 5' }}>
						<HStack spacing={3} mb={5}>
							<Img src={OpPilotCRMLogo} maxW='44px' alt='Ops Pilot CRM' />
							<VStack align='flex-start' spacing={0}>
								<Text fontWeight='800' fontSize='lg' color='white' mb={0} lineHeight='1.2'>
									Ops Pilot CRM
								</Text>
								<Text fontSize='xs' color='cyan.300' mb={0} letterSpacing='0.08em'>
									AGENCY & FREELANCER COCKPIT
								</Text>
							</VStack>
						</HStack>

						<Text fontSize='sm' color='whiteAlpha.700' lineHeight='tall' maxW='md' mb={5}>
							Email, CRM, projects, invoicing, and Blogsmith WordPress AI — one
							subscription for agencies and freelancers who want less tool sprawl.
						</Text>

						<Badge
							colorScheme='cyan'
							variant='subtle'
							bg='whiteAlpha.100'
							color='cyan.200'
							px={3}
							py={1}
							rounded='full'
							fontSize='xs'
							fontWeight='600'
							textTransform='none'
							mb={5}>
							{MONEY_BACK_GUARANTEE.shortLabel}
						</Badge>

						<HStack spacing={3} flexWrap='wrap'>
							<Button
								size='md'
								bg='cyan.400'
								color='gray.900'
								rounded='full'
								px={6}
								rightIcon={<FiArrowRight />}
								_hover={{ bg: 'cyan.300', transform: 'translateY(-1px)' }}
								onClick={() => navigate('/signup')}>
								Choose your plan
							</Button>
							<Button
								size='md'
								variant='outline'
								color='white'
								borderColor='whiteAlpha.400'
								rounded='full'
								px={6}
								_hover={{ bg: 'whiteAlpha.100' }}
								onClick={() => navigate('/auth')}>
								Log in
							</Button>
						</HStack>
					</Box>

					<Box gridColumn={{ lg: 'span 3' }}>
						<Text
							fontSize='xs'
							fontWeight='700'
							textTransform='uppercase'
							letterSpacing='0.14em'
							color='whiteAlpha.500'
							mb={4}>
							Explore
						</Text>
						<VStack align='flex-start' spacing={3}>
							{NAV_LINKS.map((link) => (
								<Link
									key={link.href}
									as='button'
									type='button'
									onClick={() => scrollTo(link.href)}
									sx={footerLinkSx}>
									{link.label}
								</Link>
							))}
						</VStack>
					</Box>

					<Box gridColumn={{ lg: 'span 2' }}>
						<Text
							fontSize='xs'
							fontWeight='700'
							textTransform='uppercase'
							letterSpacing='0.14em'
							color='whiteAlpha.500'
							mb={4}>
							Account
						</Text>
						<VStack align='flex-start' spacing={3}>
							<Link onClick={() => navigate('/signup')} sx={footerLinkSx} cursor='pointer'>
								Create account
							</Link>
							<Link onClick={() => navigate('/auth')} sx={footerLinkSx} cursor='pointer'>
								Log in
							</Link>
							<Link
								as='button'
								type='button'
								onClick={() => scrollTo('#pricing')}
								sx={footerLinkSx}>
								Pricing
							</Link>
						</VStack>
					</Box>

					<Box gridColumn={{ lg: 'span 2' }}>
						<Text
							fontSize='xs'
							fontWeight='700'
							textTransform='uppercase'
							letterSpacing='0.14em'
							color='whiteAlpha.500'
							mb={4}>
							Connect
						</Text>
						<VStack align='flex-start' spacing={4}>
							<HStack spacing={2}>
								<Icon as={FiMail} color='cyan.400' boxSize={4} />
								<Link
									href='mailto:support@opspilotcrm.com'
									fontSize='sm'
									color='whiteAlpha.800'
									_hover={{ color: 'cyan.300' }}>
									support@opspilotcrm.com
								</Link>
							</HStack>
							<HStack spacing={3}>
								<SocialButton
									href='https://twitter.com'
									label='Twitter'
									icon={FiTwitter}
								/>
								<SocialButton
									href='https://www.linkedin.com'
									label='LinkedIn'
									icon={FiLinkedin}
								/>
								<SocialButton
									href='https://www.instagram.com'
									label='Instagram'
									icon={FiInstagram}
								/>
							</HStack>
						</VStack>
					</Box>
				</SimpleGrid>

				<Text fontSize='xs' color='whiteAlpha.600' mb={6} maxW='3xl'>
					Gmail integration: users can connect their Gmail account to send outreach
					emails they write in Ops Pilot from their own address. We only request
					permission to send email, never to read your inbox, and you can disconnect
					at any time. See our{' '}
					<Link href='/privacy' color='cyan.300'>
						Privacy Policy
					</Link>
					.
				</Text>

				<Divider borderColor='whiteAlpha.150' mb={6} />

				<Flex
					direction={{ base: 'column', md: 'row' }}
					justify='space-between'
					align={{ base: 'flex-start', md: 'center' }}
					gap={4}>
					
					<VStack align='flex-start' spacing={1} flexWrap='wrap'>
						<Text fontSize='xs' color='whiteAlpha.500' mb={0}>
							© {year} Ops Pilot CRM. All rights reserved.
						</Text>
						
							<Text fontSize='xs' color='whiteAlpha.500' mb={0} display={{ base: 'none', sm: 'block' }}>
							A software by{' '}
							<Link href='https://extrawebsolutions.co.uk/' target='_blank' fontSize='xs' color='whiteAlpha.500' mb={0}>
								Extra Web Solutions
							</Link>
						</Text>
					</VStack>
				
					<HStack spacing={5} flexWrap='wrap'>
						<HStack spacing={4}>
							<Link href='/terms' fontSize='xs' sx={footerLinkSx}>
								Terms
							</Link>
							<Link href='/privacy' fontSize='xs' sx={footerLinkSx}>
								Privacy
							</Link>
							<Link href='/cookies' fontSize='xs' sx={footerLinkSx}>
								Cookies
							</Link>
						</HStack>
					</HStack>
				</Flex>
			</Container>
		</Box>
	);
};

export default Footer;
